import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Column, DataTableInput, DataTableOutput, Order, Search } from '@core/models/datables';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { Ingredient, IngredientClass } from "../models";
import { IngredientSupplier } from "../models/ingredient-supplier";
import { Supplier } from "@common/models";
import { TranslateService } from "@ngx-translate/core";


@Injectable({ providedIn: "root" })
export class IngredientService {
    private lang: string = this._translateService.currentLang;
    private columns: Column[];

    getColumns() {
        this._translateService.onLangChange.subscribe(value => {
            if (value) {
                this.lang = value.lang;
            }
        });

        this.columns = [
            {
                data:this.lang === "es" ? "name": "nameEnglish",
                name: "Nombre",
                searchable: true,
                orderable: true,
                search: new Search()
            },
            {
                data: "code",
                name: "Codigo",
                searchable: true,
                orderable: true,
                search: new Search()
            },
            {
                data: this.lang === "es" ? "description" : "descriptionEnglish",
                name: "Descripcion",
                searchable: true,
                orderable: true,
                search: new Search()
            },
            {
                data: "status.code",
                name: "status",
                searchable: true,
                orderable: true,
                search: { regex: false, value: "ACT" }
            }
        ]
    }
    /**
     * 
     * @param _http 
     */
    constructor(
        private _http: HttpClient,
        private _translateService: TranslateService) {}

    /**
     * Get all Ingredients
     * 
     */
    getAllIngredients(page: number, quantity: number, order: Order, searchText: string) {
        const input = this.createDataTableInput(page, quantity, order, searchText);
        return this._http.post<DataTableOutput<Ingredient>>(`${environment.rydApiUrl}/api/ingredient/index`, input);
    }

    getIngredientById(id: number) {
        return this._http.get<Ingredient>(`${environment.rydApiUrl}/api/ingredient/${id}`);
    }

    deleteIngredient(id: number) {
        return this._http.put<any>(`${environment.rydApiUrl}/api/ingredient/delete/${id}`, null);
    }

    addIngredient(ingredient: Ingredient) {
        return this._http.post<any>(`${environment.rydApiUrl}/api/ingredient`, ingredient);
    }

    editIngredient(ingredient: Ingredient) {
        return this._http.put<Ingredient>(`${environment.rydApiUrl}/api/ingredient/${ingredient.id}`, ingredient);
    }

    getSuppliers(ingredientId: number) {
        return this._http.get<any[]>(`${environment.rydApiUrl}/api/ingredient-supplier/${ingredientId}`);
    }

    assignSupplier(ingredientId: number, suppliers: Supplier[]) {
        return this._http.put(`${environment.rydApiUrl}/api/ingredient-supplier/${ingredientId}`, suppliers);
    }

    deleteSupplier(ingredientId: number, supplierId: Supplier[]) {
        return this._http.delete(`${environment.rydApiUrl}/api/ingredient-supplier/${ingredientId}/${supplierId}`);
    }

    downloadFile(downloadLink: string): Observable<Blob> {
        return this._http.get(downloadLink, { responseType: 'blob' });
    }
    getByName(name: string) {
        return this._http.get<Ingredient[]>(`${environment.rydApiUrl}/api/ingredient-filter/${name}`);

    }

    getIngredientAndSupplier(id: number) {
        return this._http.get<IngredientSupplier[]>(`${environment.rydApiUrl}/api/ingredient-supplier/${id}`);
    }

    getIngredientClass(){
        return this._http.get<IngredientClass[]>(`${environment.rydApiUrl}/api/ingredient-class`);
    }

    getByTechnologicalFunction(technologicalFunctionName: string, ingredientName: string) {
        if (ingredientName){
            return this._http.get<Ingredient[]>(`${environment.rydApiUrl}/api/ingredient/${ingredientName}/technological-function/${technologicalFunctionName}`);
        }
        return this._http.get<Ingredient[]>(`${environment.rydApiUrl}/api/ingredient/technological-function/${technologicalFunctionName}`);
    }

    getNutrientByTechnologicalFunction(technologicalFunctionName: string, ingredientName: string) {
        if (ingredientName){
            return this._http.get<Ingredient[]>(`${environment.rydApiUrl}/api/nutrient/${ingredientName}/technological-function/${technologicalFunctionName}`);
        }
        return this._http.get<Ingredient[]>(`${environment.rydApiUrl}/api/nutrient/technological-function/${technologicalFunctionName}`);
    }

    /**
   * Función que crea una entrada para datatable por medio de parametros
   * @param page Indica el número de página
   * @param quantity Indica la cantidad de unidades a solicitar
   * @param order Indica el orden que tendrá la columna
   * @param searchText Indica el texto que se va a filtrar
   * @returns Devuelve una entrada para datatable
   */
    private createDataTableInput(page: number, quantity: number, order: Order, searchText: string): DataTableInput {
        this.getColumns();
        let input = new DataTableInput();
        input.start = page * quantity;
        input.length = quantity;
        if (searchText !== '') {
            input.search.regex = true;
            input.search.value = searchText;
        }
        if (order) {
            input.order.push(order);
        }
        input.columns = this.columns;
        return input;
    }

}
